<template>
  <div :class="getLevelName()">
    <div v-if="qualificationFunction('showAccountInfo')">
      <div class="account" :class="{'mobile_account' : isMobile}">
        <div class="account_info">
          <div>
            <span>
              NAME
            </span>
            <span>
              {{ accessedMemberInfo.name }}
            </span>
          </div>
          <div>
            <span>
              NICKNAME
            </span>
            <div v-if="showPanel.isEditNickname">
              <div class="input_box">
                <input
                  class="edit_nickname_input" type="text"
                  v-model="editNickname"
                  @input="inputValueAni"
                  ref="inputBoxInputEl"
                >
                <button
                  type="button" title="확인"
                  :disabled="qualificationFunction('disabledEditNickname')"
                  :class="{ opacity : qualificationFunction('disabledEditNickname') }"
                  @click="submitNickname"
                >
                  <i class="xi-check" />
                </button>
                <button
                  class="edit_nickname_cancel"
                  type="button" title="취소"
                  @click="showPanel.isEditNickname = false"
                >
                  <i class="xi-close" />
                </button>
              </div>
            </div>
            <span v-else>
              {{ accessedMemberInfo.nickname }}
              <button
                type="button" title="수정"
                @click="editNicknameOpen"
              >
                <i class="xi-pen" />
              </button>
            </span>
          </div>
          <div>
            <span>
              EMAIL
            </span>
            <span>
              {{ accessedMemberInfo.email }}
            </span>
          </div>
          <div v-if="accessedMemberInfo.award">
            <span>
              AWARD
            </span>
            <span>
              {{ accessedMemberInfo.award }}
            </span>
          </div>
        </div>
        <ul
          v-if="qualificationFunction('showBenefits')"
          class="benefit"
        >
          <li v-for="(benefit,index) in benefitsList(BENEFITS)" :key="`benefit_${index}`">
            {{ benefit }}
          </li>
        </ul>
      </div>
    </div>
    <button
      type="button" class="password_edit_btn"
      style="pointer-events: auto"
      :disabled="disabledQualification()"
      @click="showPanel.isEditPassword = true"
    >
      비밀번호 변경
      <i class="xi-angle-right" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { BenefitsType } from '@/interface/member';
import BENEFITS from '@/common/constants';
import useCommonStore from '@/store/storeCommon';
import useMemberStore from '@/store/storeMember';


const { accessedMemberInfo, getLevelName, accessEditNickname } = useMemberStore();
const { editNickname } = storeToRefs(useMemberStore());
const { isMobile, isImageMode, isMainLogo, isSatelliteInfo } = storeToRefs(useCommonStore());
const { disabledQualification, inputValueAni, showPanel, showModal, modalContents } = useCommonStore();

const inputBoxInputEl = ref<HTMLInputElement | null>(null);
const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

watch(inputBoxInputEl, (input) => {
  if (input) {
    input.focus();
    input.value = accessedMemberInfo.nickname;
    if (input.value) {
      input.parentElement?.classList.add('active');
    } else {
      input.parentElement?.classList.remove('active');
    }
  }
});

/**
 * 수정한 nickname을 저장합니다.
 */
const submitNickname = async (): Promise<void> => {
  try {
    const response = await accessEditNickname(editNickname.value, accessToken);

    if (response) {
      const { nickname } = response;
      accessedMemberInfo.nickname = nickname;
      showPanel.isEditNickname = false;
      editNickname.value = '';

      isMainLogo.value = true;
      isSatelliteInfo.value = false;

      showModal.isSuccess = true;
      Object.assign(modalContents, {
        title: 'Success!',
        description: '닉네임 변경이 성공하였습니다.',
      });
    }
  } catch (e) {
    console.log(e);
  }
};

/**
 * 'BENEFITS'에 저장된 정보에 따른 혜택이 각각의 등급에 따라 필터되어 보여집니다.
 * @param lists
 */
const benefitsList = (lists: BenefitsType[]): string[] => {
  switch (accessedMemberInfo.level) {
    case '데브라':
    case '메테':
    case '코롱이':
      const levelBenefits = lists.filter(list => list.level === accessedMemberInfo.level);
      return levelBenefits[0].benefits;
    case '관리자':
      return ['ADMIN'];
    default:
      return ['GUEST'];
  }
};

/**
 * 닉네임 수정 Input을 활성화 합니다.
 */
const editNicknameOpen = (): void => {
  showPanel.isEditNickname = true;
  if (editNickname.value) {
    editNickname.value = '';
  }
};

/**
 * 조건별 함수
 */
const qualificationFunction = (qualification: string): boolean => {
  switch (qualification) {
    case 'showAccountInfo':
      return (showPanel.isAccountInfo || showPanel.isMobileAccountInfo) && !isImageMode.value;
    case 'showBenefits':
      return accessedMemberInfo.level === '데브라' || accessedMemberInfo.level === '메테' || accessedMemberInfo.level === '코롱이';
    case 'disabledEditNickname':
      return !editNickname.value || accessedMemberInfo.nickname === editNickname.value;
    default:
      return false;
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_colors.scss';

.account {
  color: $color-white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &_info {
    > div {
      line-height: 1.5;
      font-size: 16px;
      font-family: "Pretendard Medium 500", sans-serif;
      display: flex;
      align-items: center;
      gap: 10px;

      &:nth-child(3) {
        white-space: nowrap;
      }

      span {
        display: inline-block;

        &:first-child {
          color: $color-mete;
          min-width: 90px;
        }

        &:nth-child(2) {
          color: $color-white;
          text-align: left;
        }
      }

      .edit_nickname_input {
        font-size: 16px;
        color: $color-white;
      }

      .edit_nickname_cancel {
        margin-left: 5px;
      }
    }

    .input_box {
      width: 130px;
      margin-bottom: 0;
      padding-left: 0;
      position: relative;

      &:before {
        background: $color-mete;
      }

      input {
        padding: 0 50px 0 0;
      }

      button {
        position: absolute;
        right: 0;

        &:nth-child(2) {
          right: 25px;

          &.opacity {
            opacity: 0.3;
          }
        }

        &:last-child {
          right: 0;
        }
      }
    }

    button, input {
      pointer-events: auto;
    }
  }

  .benefit {
    width: 100%;
    min-width: 214px;
    max-height: calc(100dvh - 550px);
    margin-top: 6%;
    overflow: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.4);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
    padding: 14.3px 0;
    color: $color-mete;
    font-size: 12px;
    line-height: 1.50;
    white-space: nowrap;

    li {
      list-style: inside;
    }
  }

  &.mobile_account {
    .benefit {
      max-height: calc(100dvh - 640px);
    }
  }
}

.DEBRA {
  .account {
    &_info {
      > div {
        span {
          &:first-child {
            color: $color-debra;
          }
        }
      }

      .input_box {
        &:before {
          background: $color-debra;
        }
      }
    }

    .benefit {
      color: $color-debra;

      &::-webkit-scrollbar-thumb {
        background: $color-debra;
      }
    }
  }
}

.METE {
  .account {
    &_info {
      > div {
        span {
          &:first-child {
            color: $color-mete;
          }
        }
      }

      .input_box {
        &:before {
          background: $color-mete;
        }
      }
    }

    .benefit {
      color: $color-mete;

      &::-webkit-scrollbar-thumb {
        background: $color-mete;
      }
    }
  }
}

.KORONG {
  .account {
    &_info {
      > div {
        span {
          &:first-child {
            color: $color-korong;
          }
        }
      }

      .input_box {
        &:before {
          background: $color-korong;
        }
      }
    }

    .benefit {
      color: $color-korong;

      &::-webkit-scrollbar-thumb {
        background: $color-korong;
      }
    }
  }
}

.password_edit_btn {
  color: $color-white;
  font-size: 12px;
  margin-top: 10px;
  margin-left: auto;
  display: flex;
  align-items: center;

  i {
    margin-left: 5px;
  }
}

@media (min-width: 1921px) {
  .account {
    .benefit {
      margin-top: 15%;
    }
  }
}

// mobile 세로 모드
@media screen and (min-width: 250px) and (max-width: 499px) and (min-height: 450px) {
  .account {
    &_info {
      > div {
        font-size: 0.85rem;

        .edit_nickname_input {
          font-size: 0.85rem;
        }
      }
    }

    &.mobile_account {
      .benefit {
        max-height: calc(100dvh - 555px);
        font-size: 0.65rem;
        margin-top: 15px;
        padding: 10px 0;
      }
    }
  }
}

// mobile 세로 모드 Gallaxy Fold
@media screen and (min-width: 279px) and (max-height: 654px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .account {
    &.mobile_account {
      .benefit {
        max-height: calc(100dvh - 300px);
      }
    }
  }
}

// tablet
@media screen and (min-width: 500px) and (min-height: 500px) {
  .account {
    &.mobile_account {
      .benefit {
        max-height: calc(100dvh - 600px);
      }
    }
  }
}

// Surface Duo
@media screen and (min-width: 540px) and (max-height: 720px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .account {
    .benefit {
      max-height: calc(100dvh - 620px);
    }
  }
}
</style>